import { useCallback, useRef } from "react";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/shallow";

import { useBoundStore } from "@/app/store";

interface UseNotesTrashHandlersProps {
  setSelectedNotes: (notes: Set<number> | ((prev: Set<number>) => Set<number>)) => void;
  isSelectionMode: boolean;
  setIsSelectionMode: (mode: boolean) => void;
}

export function useNotesTrashHandlers({
  setSelectedNotes,
  isSelectionMode,
  setIsSelectionMode,
}: UseNotesTrashHandlersProps) {
  const layoutScrollTop = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const deviceType = useBoundStore(useShallow((state) => state.device.type));

  const handleNoteSelect = useCallback(
    (noteId: number) => {
      setSelectedNotes((prev) => {
        const newSelected = new Set(prev);
        if (newSelected.has(noteId)) {
          newSelected.delete(noteId);
        } else {
          newSelected.add(noteId);
        }

        // Exit selection mode if no notes are selected
        if (newSelected.size === 0) {
          setIsSelectionMode(false);
        } else if (!isSelectionMode) {
          setIsSelectionMode(true);
        }

        return newSelected;
      });
    },
    [isSelectionMode, setSelectedNotes, setIsSelectionMode]
  );

  const handleLongPress = useCallback(
    (noteId: number) => {
      if (deviceType === "mobile") {
        handleNoteSelect(noteId);
      }
    },
    [deviceType, handleNoteSelect]
  );

  const handlePress = useCallback(
    (id: number, _wasKeyboard?: boolean, ctrlKey?: boolean, longPress?: boolean) => {
      // Handle long press on mobile
      if (longPress) {
        handleLongPress(id);
        return;
      }

      // Handle Ctrl+click on desktop for selection
      if (ctrlKey && deviceType === "desktop") {
        handleNoteSelect(id);
        return;
      }

      // Handle selection mode clicks
      if (isSelectionMode) {
        handleNoteSelect(id);
        return;
      }

      // Normal navigation
      navigate(`/notes/trash/${id}`);
    },
    [handleLongPress, deviceType, handleNoteSelect, isSelectionMode, navigate]
  );

  return {
    layoutScrollTop,
    handleNoteSelect,
    handleLongPress,
    handlePress,
  };
}
